import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { LogOut, ChevronDown, Lock, Menu as MenuIcon } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Avatar, Dropdown, Layout, Drawer } from 'antd'
import { Button } from '@/components/ui/button'
import type { MenuProps } from 'antd'
import { currentUserAtom } from '@/store/auth.atom'
import { isPasswordModalOpenAtom } from '@/store/password-modal.atom'
import { useLogout } from '@/hooks/use-logout'
import { MainMenu } from './MainMenu'

const { Header: AntHeader } = Layout

export function Header() {
  const user = useAtomValue(currentUserAtom)
  const setIsPasswordModalOpen = useSetAtom(isPasswordModalOpenAtom)
  const { mutate: logout } = useLogout()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

  const initials = user?.username
    ? user.username
      .split(' ')
      .slice(-2)
      .map((s: string) => s[0])
      .join('')
    : 'A'

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'change-password',
      label: 'Đổi mật khẩu',
      icon: <Lock size={16} />,
      className: 'hover:!text-[#CE7A58] hover:!bg-[rgba(233,122,74,0.12)]',
      onClick: () => setIsPasswordModalOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Đăng xuất',
      icon: <LogOut size={16} />,
      className: 'hover:!text-[#CE7A58] hover:!bg-[rgba(233,122,74,0.12)]',
      onClick: () => logout(),
    },
  ]

  const userDropdown = (
    <Dropdown
      menu={{
        items: userMenuItems,
        className: '!bg-white',
      }}
      placement="bottomRight"
      trigger={['click']}
      onOpenChange={setIsUserMenuOpen}
    >
      <div className="relative cursor-pointer flex group">
        <Avatar className="bg-[#2b2b2b] text-white" size={36}>
          {initials}
        </Avatar>

        <div
          className={`absolute -right-[2px] -bottom-[2px] bg-white rounded-full w-[14px] h-[14px] flex items-center justify-center shadow-[0_0_2px_rgba(0,0,0,0.2)`}
        >
          <ChevronDown
            size={10}
            color="#2b2b2b"
            className={`transition-transform ${isUserMenuOpen ? 'rotate-90' : ''
              }`}
          />
        </div>
      </div>
    </Dropdown>
  )

  return (
    <AntHeader className="sticky top-0 z-40 bg-[#f5f2ee] px-6 py-3 flex items-center justify-between h-auto leading-normal gap-4">
      <div className="flex items-center gap-3 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          icon={<MenuIcon size={20} className="text-[#1E2F41]" />}
          onClick={() => setIsMenuOpen(true)}
          className="lg:hidden"
        />
        <Link
          to="/dashboards"
          className="flex items-center gap-2 no-underline"
        >
          <div className="flex items-center">
            <img
              src="/logo_tthcc 1.png"
              alt="CMS Logo"
              className="hidden lg:block xl:hidden h-10 xl:h-11 w-auto"
            />
            <img
              src="/logo.png"
              alt="CMS Logo"
              className="hidden lg:hidden xl:block h-10 xl:h-11 w-auto"
            />
          </div>
        </Link>
      </div>

      <div className="hidden lg:flex flex-1 justify-center px-1 min-w-0 overflow-x-auto no-scrollbar">
        <MainMenu mode="horizontal" />
      </div>

      {/* Right: User */}
      <div className="hidden lg:flex items-center gap-3 shrink-0">
        {userDropdown}
      </div>

      <Drawer
        title={
          <Link
            to="/dashboards"
            className="flex items-center gap-2 no-underline"
            onClick={() => setIsMenuOpen(false)}
          >
            <img
              src="/logo_tthcc 1.png"
              alt="CMS Logo"
              className="h-9 w-auto"
            />
          </Link>
        }
        placement="left"
        onClose={() => setIsMenuOpen(false)}
        open={isMenuOpen}
        size={300}
        closable={false}
        extra={userDropdown}
        styles={{ body: { padding: '16px' } }}
        className="lg:hidden"
      >
        <MainMenu
          mode="vertical"
          onItemClick={() => setIsMenuOpen(false)}
        />
      </Drawer>
    </AntHeader>
  )
}